import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1633354323395-d3524fb779b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxlY28lMjBob3RlbCUyMGV4dGVyaW9yJTIwbW9kZXJufGVufDB8fHx8MTc4NTkyMTU2M3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaStep(true);
        toast.info("A verification code has been sent to your email");
      } else {
        login(data.token, data.user);
        navigate("/");
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { email, code });
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="Fern Hotel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2E1B]/95 via-[#0F2E1B]/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
            One standard. Every property.
          </h2>
          <p className="mt-3 text-white/70 max-w-md text-sm leading-relaxed">
            Digital audits, live compliance scores and corrective action tracking across the entire Fern Hotels portfolio.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16 bg-[#FBFBF9]">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-[#14532D] flex items-center justify-center">
              <Leaf className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <p className="font-extrabold text-lg tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>Fern Hotels</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-[0.15em]">Audit Management Platform</p>
            </div>
          </div>

          {!mfaStep ? (
            <form onSubmit={submit} className="space-y-5">
              <h1 className="text-2xl font-bold tracking-tight text-stone-900">Sign in</h1>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@fernhotels.com" className="bg-white" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" data-testid="login-password-input" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white pr-10" />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>
              {error && <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>}
              <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-[#14532D] hover:bg-[#166534]">
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyMfa} className="space-y-5">
              <div className="flex items-center gap-2 text-[#14532D]">
                <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
                <h1 className="text-2xl font-bold tracking-tight text-stone-900">Verify it's you</h1>
              </div>
              <p className="text-sm text-stone-500">Enter the 6-digit code sent to {email}.</p>
              <Input data-testid="mfa-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-white text-center text-xl tracking-[0.5em] font-mono" />
              {error && <p data-testid="mfa-error" className="text-sm text-rose-600">{error}</p>}
              <Button data-testid="mfa-submit-button" type="submit" disabled={loading || code.length !== 6} className="w-full bg-[#14532D] hover:bg-[#166534]">
                {loading ? "Verifying..." : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => setMfaStep(false)} className="text-sm text-stone-500 hover:text-stone-800 w-full text-center">Back to login</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
